<template>
  <div dir="rtl" class="h-full bg-slate-50 md:flex md:items-center md:justify-center relative overflow-x-hidden font-sans text-slate-900 selection:bg-pink-200">

    <!-- Background Ambient Glows (static, no animation to avoid GPU thrashing) -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-96 h-96 bg-pink-200/40 rounded-full blur-3xl opacity-70"></div>
      <div class="absolute top-40 -left-20 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl opacity-70"></div>
    </div>

    <!-- Main Card Container -->
    <div class="relative z-10 w-full md:max-w-6xl md:h-[800px] flex flex-col md:flex-row bg-white md:rounded-[2.5rem] md:shadow-2xl md:border border-slate-100 md:overflow-hidden">

      <!-- Right Side: Registration Form -->
      <div class="w-full md:w-1/2 flex flex-col bg-white relative md:h-full">
        <div class="flex flex-col w-full md:h-full relative sm:px-6 sm:mt-2">

          <!-- Progress Header -->
          <div class="flex-none px-6 sm:px-12 pt-6 sm:pt-10 pb-5 bg-white z-20 sticky top-0">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm font-bold text-slate-900">{{ stepLabel }}</span>
              <span class="text-xs font-semibold text-slate-400 tabular-nums">{{ active }} / 3</span>
            </div>
            <!-- Track -->
            <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-gradient-to-r from-pink-500 to-purple-600 rounded-full transition-all duration-500 ease-out"
                :style="{ width: `${(active / 3) * 100}%` }"
              />
            </div>
          </div>

          <!-- Panel Content -->
          <div class="w-full md:flex-1 md:overflow-y-auto bg-white">
            <div class="w-full max-w-md mx-auto py-6 sm:py-8 px-4 sm:px-6 h-full flex flex-col justify-start">
              <register-form v-if="active === 1" class="w-full"/>
              <register-business-form v-else-if="active === 2" class="w-full"/>
              <validate-business v-else-if="active === 3" class="w-full" />
            </div>
          </div>

        </div>
      </div>

      <!-- Left Side: Premium Visuals (Desktop Only) -->
      <div v-if="showDesktopPanel" class="hidden md:flex w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900 p-12 flex-col justify-between relative overflow-hidden text-white">

        <!-- Abstract Decorations (reduced blur for Chrome perf) -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>

        <!-- Text Content -->
        <div class="z-10 relative mt-8">
          <h2 class="text-4xl lg:text-5xl font-black leading-tight mb-4 tracking-tight">
            3 שלבים קצרים,<br/>והיומן שלכם מסודר.
          </h2>
          <p class="text-slate-300 text-lg leading-relaxed max-w-sm">
            הצטרפו לבעלי עסקים שכבר הפסיקו לרדוף אחרי תורים, והתחילו לנהל את העסק בקלות.
          </p>
        </div>

        <!-- Brand New Visual Representation -->
        <div class="z-10 relative flex-1 flex flex-col items-center justify-center mt-10 space-y-5">
          <!-- Notification Card 1 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-green-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-green-400/20 p-3 rounded-xl text-green-300">
                <Layout class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">דף נחיתה אישי</p>
                <p class="text-slate-300 text-xs mt-0.5">הלקוחות קובעים לבד, 24/7</p>
              </div>
            </div>
          </div>

          <!-- Notification Card 2 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform -translate-x-6 transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-pink-500"></div>
            <div class="flex items-center gap-4">
              <div class="bg-pink-500/20 p-3 rounded-xl text-pink-300">
                <MessageCircle class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">סוף להברזות מרגיזות</p>
                <p class="text-slate-300 text-xs mt-0.5">תזכורות וואטסאפ אוטומטיות ללקוחות</p>
              </div>
            </div>
          </div>

          <!-- Notification Card 3 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform translate-x-4 transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-blue-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-blue-400/20 p-3 rounded-xl text-blue-300">
                <CreditCard class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">שליטה מלאה בהכנסות</p>
                <p class="text-slate-300 text-xs mt-0.5">סליקה, חשבוניות ירוקות וגביית דמי ביטול</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Login Link -->
        <div class="z-10 relative mt-12">
          <div class="bg-white/10 rounded-2xl p-5 border border-white/10 flex justify-between items-center">
            <div>
              <p class="font-bold text-white mb-0.5">יש לכם כבר משתמש?</p>
              <p class="text-sm text-slate-400">חזרו לעניינים בלחיצת כפתור.</p>
            </div>
            <router-link
              class="bg-white text-slate-900 hover:bg-slate-100 font-bold py-3 px-6 rounded-xl transition-colors shadow-lg text-sm"
              :to="{ name: AuthRouteNames.AdminLogin }">
              להתחברות למערכת
            </router-link>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Session from 'supertokens-web-js/recipe/session';
import { useRoute, useRouter } from 'vue-router';
import { AdminRouteNames, AuthRouteNames } from '@app/router/route-names';
import { useResponsive } from "@app/composables/responsive";
import ValidateBusiness from "@app/components/auth/ValidateBusiness.vue";
import RegisterForm from "@app/components/auth/RegisterForm.vue";
import RegisterBusinessForm from "@app/components/auth/RegisterBusinessForm.vue";

// Icons
import { MessageCircle, CreditCard, Layout } from 'lucide-vue-next';

const router = useRouter();
const route = useRoute()
const { isMobile, screenWidth } = useResponsive();

const active = ref(1);

const stepLabels: Record<number, string> = { 1: 'פרטים אישיים', 2: 'פרטי העסק', 3: 'אימות אחרון' };
const stepLabel = computed(() => stepLabels[active.value] ?? '');

const checkForSession = async () => {
  const sessionExists = await Session.doesSessionExist();
  if (active.value === 1 && sessionExists) {
    await router.replace({ name: AdminRouteNames.Home });
  } else if (active.value > 1 && !sessionExists) {
    // Accessed step 2/3 without completing step 1 — send back to start
    await router.replace({ name: AuthRouteNames.Register });
  }
};

const showDesktopPanel = computed(() => {
  return screenWidth.value > 768;
});

checkForSession();

watch(() => route.query.step, (step) => {
  if (step === 'business')
    active.value = 2;
  else if (step === 'validate')
    active.value = 3;
  else
    active.value = 1;
}, {flush: 'pre', immediate: true})

</script>

